import { faChartMixed, faSparkles } from "@fortawesome/pro-regular-svg-icons";
import type { Layout } from "react-grid-layout";

import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardLayoutEditor } from "src/analytics/dashboard/DashboardLayoutEditor";
import { sortPinsByPosition } from "src/analytics/dashboard/dashboardLayout";
import { StaticPinGrid } from "src/analytics/dashboard/StaticPinGrid";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import type { Realm } from "src/clients/analytics-dashboard";
import { EmptyState } from "src/design-system/EmptyState";
import { WhitePane } from "src/jobs/common/WhitePane";

export const DashboardGrid: React.FC<{
  realm: Realm;
  realmId: string;
  labelDictionaries: LabelDictionaries;
  isEditing?: boolean;
  isSaving?: boolean;
  editLayout?: Layout;
  onEditLayoutChange?: (layout: Layout) => void;
  dashboardId?: string;
  onOpenCopilot?: () => void;
}> = ({
  realm,
  realmId,
  labelDictionaries,
  isEditing = false,
  isSaving = false,
  editLayout,
  onEditLayoutChange,
  dashboardId,
  onOpenCopilot,
}) => {
  const { dashboard, isLoading } = useDashboard(realm, realmId, dashboardId);
  const pins = sortPinsByPosition(Object.values(dashboard.pins));

  if (isLoading) {
    return <DashboardGridSkeleton />;
  }

  if (pins.length === 0) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          actionButton={
            onOpenCopilot
              ? {
                  label: "Create with Copilot",
                  icon: faSparkles,
                  onClick: onOpenCopilot,
                }
              : undefined
          }
          description="Create a chart with Copilot and pin to your dashboard."
          headline="Create with Copilot"
          icon={faChartMixed}
        />
      </WhitePane>
    );
  }

  return isEditing && editLayout && onEditLayoutChange ? (
    <DashboardLayoutEditor
      dashboardId={dashboardId}
      isSaving={isSaving}
      labelDictionaries={labelDictionaries}
      layout={editLayout}
      onLayoutChange={onEditLayoutChange}
      pins={pins}
      realm={realm}
      realmId={realmId}
    />
  ) : (
    <div
      className="decideScrollbar h-full overflow-y-auto"
      data-loc="dashboard-grid"
    >
      <StaticPinGrid
        dashboardId={dashboardId}
        labelDictionaries={labelDictionaries}
        pins={pins}
        realm={realm}
        realmId={realmId}
      />
    </div>
  );
};
